import React, { useEffect, useState } from 'react';
import {
  Box, Flex, Text, Image, Spinner, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Button, ButtonGroup, useColorModeValue,
} from '@chakra-ui/react';
import { getResourcePreviewUrl, getResourcePreviewBlob, getResourcePreviewData } from '../../api/resourceService';

const MAX_TABLE_ROWS = 50;

/**
 * Embed address for YouTube and Vimeo links, or null for other sites
 * (most websites refuse to be shown inside another page).
 */
export const videoEmbedUrl = (url) => {
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const host = parsed.hostname.replace(/^(www\.|m\.)/, '');
  let youtubeId = null;
  if (host === 'youtu.be') youtubeId = parsed.pathname.slice(1);
  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    youtubeId = parsed.searchParams.get('v')
      || parsed.pathname.match(/^\/(?:embed|shorts|live)\/([^/]+)/)?.[1];
  }
  if (youtubeId && /^[\w-]{6,20}$/.test(youtubeId)) {
    return `https://www.youtube-nocookie.com/embed/${youtubeId}`;
  }
  const vimeoId = host === 'vimeo.com' && parsed.pathname.match(/^\/(\d+)/)?.[1];
  return vimeoId ? `https://player.vimeo.com/video/${vimeoId}` : null;
};

/**
 * Split CSV/TSV text into rows, honouring quoted fields. Stops after maxRows rows.
 */
const parseDelimited = (text, delimiter, maxRows) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length && rows.length < maxRows; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i += 1; }
      else if (ch === '"') inQuotes = false;
      else field += ch;
    } else if (ch === '"' && field === '') {
      inQuotes = true;
    } else if (ch === delimiter) {
      row.push(field); field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field); rows.push(row); row = []; field = '';
    } else {
      field += ch;
    }
  }
  if ((field || row.length) && rows.length < maxRows) { row.push(field); rows.push(row); }
  return rows;
};

const prettyText = (text, truncated, filename) => {
  // A cut-off JSON file can't be parsed, so it is shown as it is
  if (!truncated && /\.json$/i.test(filename || '')) {
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      return text;
    }
  }
  return text;
};

/**
 * Address for an image/PDF/audio preview. Published files load directly (so audio streams);
 * pending ones are fetched with the login token, which a plain <img> or <audio> can't send.
 */
const usePreviewSrc = (resource, enabled) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const { id, status } = resource;

  useEffect(() => {
    setFailed(false);
    if (!enabled) {
      setSrc(null);
      return undefined;
    }
    if (status === 'published') {
      setSrc(getResourcePreviewUrl(id));
      return undefined;
    }
    let objectUrl = null;
    let cancelled = false;
    setSrc(null);
    getResourcePreviewBlob(id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, status, enabled]);

  return { src, failed, setFailed };
};

const Unavailable = ({ children }) => (
  <Text fontSize="sm" color="gray.500">{children || 'The preview could not be loaded. Download the file to view it.'}</Text>
);

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const Note = ({ children }) => <Text fontSize="xs" color="gray.500" mt={2}>{children}</Text>;

/**
 * First row as the header; rows are padded so every row has the same number of cells.
 */
const DataTable = ({ rows }) => {
  const headBg = useColorModeValue('gray.100', 'gray.700');
  const width = Math.max(0, ...rows.map((r) => r.length));
  const pad = (row) => [...row, ...Array(width - row.length).fill('')];
  const [header = [], ...body] = rows;
  return (
    <TableContainer maxH="420px" overflowY="auto" borderWidth="1px" borderRadius="md">
      <Table size="sm">
        <Thead position="sticky" top={0} bg={headBg}>
          <Tr>{pad(header).map((cell, i) => <Th key={i} textTransform="none">{cell}</Th>)}</Tr>
        </Thead>
        <Tbody>
          {body.map((row, r) => (
            <Tr key={r}>
              {pad(row).map((cell, c) => <Td key={c} maxW="320px" overflow="hidden" textOverflow="ellipsis">{cell}</Td>)}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

const CsvPreview = ({ data, filename }) => {
  const delimiter = /\.tsv$/i.test(filename || '') ? '\t' : ',';
  // Header + rows to show + one more, to tell whether there are more rows
  let rows = parseDelimited(data.text, delimiter, MAX_TABLE_ROWS + 2);
  const readAllText = rows.length <= MAX_TABLE_ROWS + 1;
  // When the whole preview text was parsed, the last line of a cut-off file is incomplete
  if (data.truncated && readAllText) rows = rows.slice(0, -1);
  const shown = rows.slice(0, MAX_TABLE_ROWS + 1);
  const bodyCount = Math.max(shown.length - 1, 0);
  return (
    <Box>
      <DataTable rows={shown} />
      <Note>
        {data.truncated || rows.length > shown.length
          ? `Showing the first ${bodyCount} rows. Download the file to see all of it.`
          : `${bodyCount} row${bodyCount === 1 ? '' : 's'}`}
      </Note>
    </Box>
  );
};

const PlainTextPreview = ({ data, filename }) => {
  const codeBg = useColorModeValue('gray.50', 'gray.900');
  return (
    <Box>
      <Box
        as="pre" bg={codeBg} p={4} borderRadius="md" maxH="420px" overflow="auto"
        fontSize="xs" whiteSpace="pre-wrap" wordBreak="break-word"
      >
        {prettyText(data.text, data.truncated, filename)}
      </Box>
      {data.truncated && <Note>Showing the first 100 KB. Download the file to see all of it.</Note>}
    </Box>
  );
};

const SheetPreview = ({ data }) => {
  const [active, setActive] = useState(0);
  const sheet = data.sheets[active];
  if (!sheet) return <Unavailable>This workbook has no sheets to show.</Unavailable>;
  return (
    <Box>
      {data.sheets.length > 1 && (
        <ButtonGroup size="xs" variant="outline" mb={3} flexWrap="wrap" spacing={2}>
          {data.sheets.map((s, i) => (
            <Button key={s.name} isActive={i === active} onClick={() => setActive(i)} mb={1}>{s.name}</Button>
          ))}
        </ButtonGroup>
      )}
      {sheet.rows.length ? <DataTable rows={sheet.rows} /> : <Unavailable>This sheet is empty.</Unavailable>}
      {(sheet.truncated || data.more_sheets) && (
        <Note>
          Showing the first {MAX_TABLE_ROWS} rows and 30 columns
          {data.more_sheets ? ' of the first 10 sheets' : ''}. Download the file to see all of it.
        </Note>
      )}
    </Box>
  );
};

// The converted document is shown in a sandboxed frame: no scripts, forms or navigation,
// and the policy below blocks loading anything from outside.
const documentPage = (html) => `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'">
<style>
  body { font-family: Georgia, 'Times New Roman', serif; line-height: 1.6; color: #1a202c; background: #fff;
         margin: 0; padding: 24px 32px; overflow-wrap: break-word; }
  table { border-collapse: collapse; margin: 12px 0; }
  td, th { border: 1px solid #cbd5e0; padding: 4px 8px; vertical-align: top; }
  img { display: none; }
</style></head><body>${html}</body></html>`;

const DocumentPreview = ({ data, title }) => (
  <Box>
    <Box
      as="iframe" title={title} sandbox="" srcDoc={documentPage(data.html)}
      w="100%" h="560px" borderWidth="1px" borderRadius="md" bg="white"
    />
    <Note>
      A simplified view without images{data.truncated ? ', showing the beginning of the document' : ''}.
      Download the file for the original layout.
    </Note>
  </Box>
);

const ArchivePreview = ({ data }) => {
  const headBg = useColorModeValue('gray.100', 'gray.700');
  return (
    <Box>
      <Text fontSize="sm" mb={2}>
        {data.total_files} file{data.total_files === 1 ? '' : 's'}, {formatBytes(data.total_size)} when unpacked
      </Text>
      <TableContainer maxH="420px" overflowY="auto" borderWidth="1px" borderRadius="md">
        <Table size="sm">
          <Thead position="sticky" top={0} bg={headBg}>
            <Tr><Th textTransform="none">Name</Th><Th textTransform="none" isNumeric>Size</Th></Tr>
          </Thead>
          <Tbody>
            {data.entries.map((e) => (
              <Tr key={e.name}>
                <Td fontFamily="mono" fontSize="xs" wordBreak="break-all" whiteSpace="normal">{e.name}</Td>
                <Td isNumeric fontSize="xs">{e.is_dir ? '' : formatBytes(e.size)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      {data.total_entries > data.entries.length && (
        <Note>Showing the first {data.entries.length} of {data.total_entries} entries.</Note>
      )}
    </Box>
  );
};

/**
 * Previews the server returns as data: the start of text/CSV files, and converted
 * Excel, Word and ZIP files.
 */
const DataPreview = ({ resource }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    getResourcePreviewData(resource.id)
      .then((result) => !cancelled && setData(result))
      // 422 means the server could not convert this particular file, and says why
      .catch((err) => !cancelled && setError(err.response?.status === 422 ? err.response.data.detail : true));
    return () => { cancelled = true; };
  }, [resource.id]);

  if (error) {
    return <Unavailable>{typeof error === 'string' ? `${error}. Download the file to view it.` : null}</Unavailable>;
  }
  if (!data) return <Flex justify="center" py={8}><Spinner /></Flex>;

  switch (resource.preview_kind) {
    case 'table': return <CsvPreview data={data} filename={resource.filename} />;
    case 'sheet': return <SheetPreview data={data} />;
    case 'document': return <DocumentPreview data={data} title={resource.title} />;
    case 'archive': return <ArchivePreview data={data} />;
    default: return <PlainTextPreview data={data} filename={resource.filename} />;
  }
};

/**
 * In-page preview of a resource: images, PDFs, audio, text/CSV, Excel, Word, ZIP contents,
 * and YouTube/Vimeo links. Renders nothing for resources that can't be previewed.
 */
const ResourcePreview = ({ resource }) => {
  const kind = resource.preview_kind;
  const isMedia = ['image', 'pdf', 'audio'].includes(kind);
  const { src, failed, setFailed } = usePreviewSrc(resource, isMedia);
  const embed = resource.kind === 'link' ? videoEmbedUrl(resource.url) : null;

  if (embed) {
    return (
      <Box position="relative" pt="56.25%" borderRadius="md" overflow="hidden">
        <Box
          as="iframe" src={embed} title={resource.title}
          position="absolute" top={0} left={0} w="100%" h="100%" border={0}
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
        />
      </Box>
    );
  }
  if (['table', 'text', 'sheet', 'document', 'archive'].includes(kind)) return <DataPreview resource={resource} />;
  if (!isMedia) return null;
  if (failed) return <Unavailable />;
  if (!src) return <Flex justify="center" py={8}><Spinner /></Flex>;

  if (kind === 'image') {
    return (
      <Flex justify="center">
        <Image src={src} alt={resource.title} maxH="520px" objectFit="contain" borderRadius="md" onError={() => setFailed(true)} />
      </Flex>
    );
  }
  if (kind === 'pdf') {
    return <Box as="iframe" src={src} title={resource.title} w="100%" h="640px" border={0} borderRadius="md" />;
  }
  return (
    <Box as="audio" controls preload="metadata" src={src} w="100%" onError={() => setFailed(true)}>
      Your browser cannot play this audio.
    </Box>
  );
};

export const hasPreview = (resource) =>
  Boolean(resource.preview_kind) || (resource.kind === 'link' && Boolean(videoEmbedUrl(resource.url)));

export default ResourcePreview;
